import { Injectable } from '@angular/core';
import { BookmarkItem, BookmarkGroup } from 'src/app/components/bookmarks/bookmarks';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from '../../services/storage.service';

const BOOKMARKS_STATE_TOKEN = 'BOOKMARKS_STATE_TOKEN';
const CLIENTID_KEY = 'clientId'; // Todo: clientId key should be defined globaly.

@Injectable({
  providedIn: 'root'
})
export class BookmarkService {
  public bookmarks$: Observable<BookmarkGroup<BookmarkItem>[]>;
  public oldBookmarks$: Observable<BookmarkGroup<BookmarkItem>[]>; // Todo: remove

  private bookmarksSubject: BehaviorSubject<BookmarkGroup<BookmarkItem>[]> = new BehaviorSubject<BookmarkGroup<BookmarkItem>[]>([]);
  private oldBookmarksSubject: BehaviorSubject<BookmarkGroup<BookmarkItem>[]> = new BehaviorSubject<BookmarkGroup<BookmarkItem>[]>(null);

  constructor(
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private storageService: StorageService,
  ) {
    this.bookmarks$ = this.bookmarksSubject.asObservable();
    this.oldBookmarks$ = this.oldBookmarksSubject.asObservable();
    this.refresh();
  }

  public addBookmark(bookmark: BookmarkItem) {
    try {
      let copy = [...this.bookmarksSubject.getValue()];
      let group = copy.find(g => g.key === bookmark.type);
      if (!group) {
        group = new BookmarkGroup<BookmarkItem>(bookmark.type);
        copy.push(group);
      }

      if (!group.members.some(b => b.id == bookmark.id)) {
        group.members.push(bookmark);
        this.bookmarksSubject.next(copy);
      }

      this.toastrService.info(
        this.translateService.instant('BO_BOOKMARK_ADDED')
      );
    } catch (err) {
      console.error(err);
      this.toastrService.error(
        this.translateService.instant('BO_BOOKMARK_ADD_FAILED')
      );
    }
    this.saveState();
  }

  public removeBookmark(bookmark: BookmarkItem) {
    try {
      let copy = [...this.bookmarksSubject.getValue()];
      let group = copy.find(g => g.key === bookmark.type);

      if (!!group && group.members.includes(bookmark)) {
        group.members = group.members.filter(b => b.id !== bookmark.id);
        if(group.members.length < 1) copy = copy.filter(g => g.key !== group.key);
        this.bookmarksSubject.next(copy);
      }

      this.toastrService.info(
        this.translateService.instant('BO_BOOKMARK_REMOVED')
      );
    } catch (err) {
      console.error(err);
      this.toastrService.error(
        this.translateService.instant('BO_BOOKMARK_REMOVE_FAILED')
      );
    }
    this.saveState();
  }

  public removeSelected(group: BookmarkGroup<BookmarkItem>) {
    try {
      let copy = [...this.bookmarksSubject.getValue()];
      let updateroup = copy.find(g => g.key === group.key);

      if(!!updateroup) {
        updateroup.members = updateroup.members.filter(b => !group.members.find(b2 => b.id === b2.id && !!b2.selected));
        if(updateroup.members.length < 1) copy = copy.filter(g => g.key !== group.key);
        this.bookmarksSubject.next(copy);
      }
      
    } catch (err) {
      console.error(err);
      this.toastrService.error(
        this.translateService.instant('BO_BOOKMARK_REMOVE_FAILED')
      );
    }
    this.saveState();
  }

  public removeBookmarkGroup(group: BookmarkGroup<BookmarkItem>) {
    try {
      let copy = [...this.bookmarksSubject.getValue()]?.filter(g => g.key !== group.key) || [];
      this.bookmarksSubject.next(copy);
      this.toastrService.info(
        this.translateService.instant('BO_BOOKMARK_REMOVED')
      );
    } catch (err) {
      console.error(err);
      this.toastrService.error(
        this.translateService.instant('BO_BOOKMARK_REMOVE_FAILED')
      );
    }
    this.saveState();
  }

  public removeOldBookmarkGroup(group: BookmarkGroup<BookmarkItem>) {
    try {
      let copy = [...this.oldBookmarksSubject.getValue()]?.filter(g => g.key !== group.key) || [];
      this.oldBookmarksSubject.next(copy);
      this.storageService.setLocalStorageItem(BOOKMARKS_STATE_TOKEN, JSON.stringify(copy));
      this.toastrService.info(
        this.translateService.instant('BO_BOOKMARK_REMOVED')
      );
    } catch (err) {
      console.error(err);
      this.toastrService.error(
        this.translateService.instant('BO_BOOKMARK_REMOVE_FAILED')
      );
    }
  }

  public refresh() {
    const clientId = this.storageService.getLocalStorageItem(CLIENTID_KEY);
    let savedState = this.storageService.getLocalStorageItem(`${BOOKMARKS_STATE_TOKEN}_${clientId}`);
    let bookmarks = !!savedState ? JSON.parse(savedState) as BookmarkGroup<BookmarkItem>[] : [];
    this.bookmarksSubject.next(bookmarks);

    // Todo: Remove when no one uses old bookmarks anymore.
    let oldSavedState = this.storageService.getLocalStorageItem(BOOKMARKS_STATE_TOKEN);
    if(!!oldSavedState) {
      let oldBookmarks = JSON.parse(oldSavedState) as BookmarkGroup<BookmarkItem>[];
      console.log("old", oldBookmarks);
      if(!!oldBookmarks && oldBookmarks.length > 0) {
        console.log("set old", oldBookmarks);
        this.oldBookmarksSubject.next(oldBookmarks);
      } else {
        this.storageService.removeLocalStorageItem(BOOKMARKS_STATE_TOKEN);
      }
    }
  }

  private saveState() {
    const clientId = this.storageService.getLocalStorageItem(CLIENTID_KEY);
    this.storageService.setLocalStorageItem(`${BOOKMARKS_STATE_TOKEN}_${clientId}`, JSON.stringify(this.bookmarksSubject.getValue() || []));
  }
}
